import { UiText } from '@experiences/ui-common';
import { useShowDialog } from '@experiences/util';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import clsx from 'clsx';
import React, {
    useCallback,
    useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';

import { notificationType } from '../../../common/constants/Constant';
import * as RouteNames from '../../../common/constants/RouteNames';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import { removeHostedLicense } from '../../../services/licensing/management/HostService';
import {
    getAllOnPremOrganizations,
    onPremOrganizationManagementUri,
} from '../../../services/organization/OnPremOrganizationService';
import UiPageContainer from '../../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../../organizationsettings/AdminBreadCrumbs';
import OnPremOrganizationDeleteDialogBody from '../subcomponents/OnPremOrganizationDeleteDialogBody';

const useStyles = makeStyles((theme) =>
    createStyles({
        deleteOrgTitle: {
            fontWeight: 600,
            fontSize: '14px',
            color: theme.palette.semantic.colorForeground,
        },
        deleteOrgActions: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        deleteOrgButton: {
            marginTop: '16px',
            marginBottom: '16px',
            color: `${theme.palette.semantic.colorErrorText} !important`,
            '&:hover': {
                backgroundColor: `${theme.palette.semantic.colorErrorBackground} !important`,
                borderColor: `${theme.palette.semantic.colorErrorText} !important`,
            },
            borderColor: `${theme.palette.semantic.colorErrorText} !important`,
            width: '120px',
        },
        spacer: { marginTop: '24px' },
        removeLicenseButton: {
            marginTop: '16px',
            marginBottom: '16px',
            color: `${theme.palette.semantic.colorWarningText} !important`,
            '&:hover': {
                backgroundColor: `${theme.palette.semantic.colorErrorBackground} !important`,
                borderColor: `${theme.palette.semantic.colorWarningText} !important`,
            },
            borderColor: `${theme.palette.semantic.colorWarningText} !important`,
            width: '120px',
        },
        infoSection: {
            display: 'flex',
            flexDirection: 'column',
            marginBottom: '32px',
        },
    }),
);

const HostOrganizationSettings: React.FC = () => {

    const classes = useStyles();
    const createDialog = useShowDialog();

    const { formatMessage: translate } = useIntl();
    const createNotification = useUiSnackBar();

    const { organizationId } = useParams() as { organizationId: string };

    const { data: organizations } = useSWR(
        { url: `${onPremOrganizationManagementUri}/getAllOrganizations` },
        getAllOnPremOrganizations,
    );

    const organization = useMemo(() => organizations?.results.find(orgIterator => orgIterator.id === organizationId)
        , [ organizationId, organizations?.results ]);

    const openDeleteDialog = useCallback(
        async () => {
            const proceed = await createDialog({
                title: translate({ id: 'CLIENT_DELETE_ORGANIZATION' }),
                customDialogContent: OnPremOrganizationDeleteDialogBody,
                customDialogContentProps: {
                    orgName: organization?.name ?? '',
                    orgId: organizationId,
                },
                icon: 'error',
                showCancel: false,
            });
            if (proceed) {
                createNotification(translate({ id: 'CLIENT_ORGANIZATION_DELETED' }), notificationType.INPROGRESS);
            }
        },
        [ createDialog, translate, organization, organizationId, createNotification ],
    );

    const openRemoveLicenseDialog = useCallback(
        async () => {
            const proceed = await createDialog({
                title: translate({ id: 'CLIENT_REMOVE_LICENSE_ALLOCATION' }),
                body: translate({ id: 'CLIENT_REMOVE_LICENSE_DIALOG' }),
                icon: 'warning',
                showCancel: true,
                primaryButtonText: translate({ id: 'CLIENT_REMOVE' }),
            });
            if (proceed) {
                await removeHostedLicense(organizationId);
                createNotification(translate({ id: 'CLIENT_LICENSE_SUCCESSFULLY_REMOVED' }), notificationType.SUCCESS);
            }
        },
        [ createDialog, translate, organizationId, createNotification ],
    );

    const breadCrumbLinks = useMemo(() => [
        {
            link: RouteNames.OrganizationHome.replace(':organizationId', organizationId),
            name: organization?.name ?? '',
        },
        {
            link: RouteNames.HostOrganizationSettings.replace(':organizationId', organizationId),
            name: translate({ id: 'CLIENT_SETTINGS' }),
        },
    ], [ organization?.name, organizationId, translate ]);

    return (
        <UiPageContainer
            position='left'
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={breadCrumbLinks} />}
            maxWidth='1100px'>
            <div className={clsx(classes.infoSection, classes.spacer)}>
                <UiText
                    className={classes.deleteOrgTitle}
                    data-cy="organization-name-title">
                    {translate({ id: 'CLIENT_ORGANIZATION_NAME' })}
                </UiText>
                <TextField
                    style={{ marginTop: '4px' }}
                    disabled
                    value={organization?.name} />
                <UiText
                    className={clsx(classes.deleteOrgTitle, classes.spacer)}
                    data-cy="admin-email-organization-title">
                    {translate({ id: 'CLIENT_ADMIN_EMAIL' })}
                </UiText>
                <TextField
                    style={{ marginTop: '4px' }}
                    disabled
                    value={organization?.companyEmail} />
            </div>
            <UiText
                className={classes.deleteOrgTitle}
                data-cy="delete-organization-title">
                {translate({ id: 'CLIENT_REMOVE_LICENSE_ALLOCATION' })}
            </UiText>
            <div className={classes.deleteOrgActions}>
                <Button
                    className={classes.removeLicenseButton}
                    onClick={openRemoveLicenseDialog}
                    data-cy="remove-organization-license-button"
                    variant="outlined"
                >
                    {translate({ id: 'CLIENT_REMOVE' })}
                </Button>
            </div>
            <UiText
                className={clsx(classes.deleteOrgTitle, classes.spacer)}
                data-cy="delete-organization-title">
                {translate({ id: 'CLIENT_DELETE_ORGANIZATION' })}
            </UiText>
            <div className={classes.deleteOrgActions}>
                <Button
                    className={classes.deleteOrgButton}
                    onClick={openDeleteDialog}
                    data-cy="delete-organization-button"
                    variant="outlined"
                >
                    {translate({ id: 'CLIENT_DELETE' })}
                </Button>
            </div>
        </UiPageContainer>
    );
};

export default HostOrganizationSettings;
